import { z } from 'zod';

export const WorkspaceTargetSchema = z.object({
  tenantId: z.string().min(1),
  tenantName: z.string(),
  targetId: z.string().min(1),
  targetType: z.string().min(1),
  targetName: z.string(),
});

export type WorkspaceTarget = z.infer<typeof WorkspaceTargetSchema>;

export const WorkspaceWindowBoundsSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

export type WorkspaceWindowBounds = z.infer<typeof WorkspaceWindowBoundsSchema>;

export const WorkspaceDescriptorSchema = WorkspaceTargetSchema.extend({
  id: z.string().min(1),
  rootDomain: z.string().min(1),
  partition: z.string().min(1),
  httpPort: z.number().int().min(1).max(65535),
  httpsPort: z.number().int().min(1).max(65535),
  isOpen: z.boolean(),
  lastOpenedAt: z.number().int().nonnegative(),
  bounds: WorkspaceWindowBoundsSchema.optional(),
});

export type WorkspaceDescriptor = z.infer<typeof WorkspaceDescriptorSchema>;

export const WORKSPACE_WINDOW_KIND = {
  WORKSPACE: 'workspace',
  PICKER: 'picker',
} as const;

export type WorkspaceWindowKind =
  (typeof WORKSPACE_WINDOW_KIND)[keyof typeof WORKSPACE_WINDOW_KIND];

export type CurrentWorkspaceInfo =
  | {
      kind: typeof WORKSPACE_WINDOW_KIND.WORKSPACE;
      workspace: WorkspaceDescriptor;
    }
  | {
      kind: typeof WORKSPACE_WINDOW_KIND.PICKER;
      tenantId: string | null;
    };

export type WorkspaceSummary = Pick<
  WorkspaceDescriptor,
  | 'id'
  | 'tenantId'
  | 'tenantName'
  | 'targetId'
  | 'targetType'
  | 'targetName'
  | 'httpPort'
  | 'httpsPort'
  | 'isOpen'
  | 'lastOpenedAt'
>;

export const toWorkspaceSummary = ({
  id,
  tenantId,
  tenantName,
  targetId,
  targetType,
  targetName,
  httpPort,
  httpsPort,
  isOpen,
  lastOpenedAt,
}: WorkspaceDescriptor): WorkspaceSummary => ({
  id,
  tenantId,
  tenantName,
  targetId,
  targetType,
  targetName,
  httpPort,
  httpsPort,
  isOpen,
  lastOpenedAt,
});

export const formatWorkspaceWindowTitle = ({
  targetName,
  tenantName,
}: Pick<WorkspaceDescriptor, 'targetName' | 'tenantName'>): string =>
  `${targetName} — ${tenantName}`;

export type OpenWorkspaceResult =
  | { status: 'opened'; workspaceId: string }
  | { status: 'focused'; workspaceId: string };

export type WorkspaceIpcResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };
